import React, { useEffect, Component } from 'react';
import { X, AlertTriangle, Package, MapPin, CheckCircle } from 'lucide-react';
import { Product, WA_NUMBER } from '../data/products';
import { WhatsAppIcon } from './icons/WhatsAppIcon';
interface ProductModalProps {
  product: Product | null;
  onClose: () => void;
}
export function ProductModal({
  product,
  onClose
}: ProductModalProps) {
  // Lock body scroll when open
  useEffect(() => {
    if (product) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [product]);
  // Close on ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  if (!product) return null;
  const waMsg = encodeURIComponent(`Hola! Quiero el ${product.name} con 20% OFF — precio $${product.currentPrice} UY. ¿Tienen stock disponible?`);
  const waLink = `https://wa.me/${WA_NUMBER}?text=${waMsg}`;
  return <div className="fixed inset-0 bg-black/90 z-[9999] overflow-y-auto p-[30px_20px] backdrop-blur-sm flex items-start justify-center" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-341" data-mp="1qcmyawhidcho">
      <div className="bg-dark-600 border border-[#333] rounded-xl max-w-[780px] w-full overflow-hidden relative m-auto animate-modal-in" data-id="element-342" data-mp="1heb7atcqccop">
        <button onClick={onClose} className="absolute top-[15px] right-[20px] bg-transparent border-none text-white text-[2rem] cursor-pointer z-20 leading-none transition-colors duration-200 hover:text-alphaGold" aria-label="Close modal" data-id="element-343" data-mp="17ekhv6310nvh">
          <X size={32} data-id="element-344" data-mp="jo6jkxtulssg" />
        </button>

        <div className="w-full h-[320px] bg-white flex items-center justify-center overflow-hidden" data-id="element-345" data-mp="jp8927bm2htp">
          <img src={product.image} alt={product.name} className="w-full h-full object-contain p-[30px]" onError={e => {
          e.currentTarget.src = product.fallbackImage;
        }} data-id="element-346" data-mp="on9sitcy8j5p" />
        </div>

        <div className="p-[35px]" data-id="element-347" data-mp="2hjvl68byqwfu">
          <div className="inline-block bg-alphaGold text-white text-[0.75rem] font-extrabold py-1 px-3.5 rounded-sm tracking-[2px] uppercase mb-[15px]" data-id="element-348" data-mp="2hkwvubp2hgx1">
            20% OFF — OFERTA LIMITADA
          </div>

          <h2 className="text-[1.6rem] font-black uppercase tracking-[2px] text-white mb-[15px]" data-id="element-349" data-mp="399qjguhstwgn">
            {product.name}
          </h2>

          <div className="bg-alphaGold/10 border-l-4 border-l-alphaGold p-[18px_20px] mb-5 rounded-r" data-id="element-350" data-mp="30amytm2zh901">
            <h4 className="text-alphaGold text-[0.85rem] uppercase tracking-[2px] mb-2 font-bold flex items-center gap-2" data-id="element-351" data-mp="2vcl94ib1ybfl">
              <AlertTriangle className="w-4 h-4" data-id="element-352" data-mp="1owslhcvjwfo0" /> El Problema que Resuelve
            </h4>
            <p className="text-[#ccc] text-[0.92rem] leading-[1.7]" data-id="element-353" data-mp="1jvv4h3ma1ro3">
              {product.pain}
            </p>
          </div>

          <p className="text-[#aaa] leading-[1.8] text-[0.97rem] mb-[25px]" data-id="element-354" data-mp="19uss5lt76ptb">
            {product.description}
          </p>

          <p className="text-[0.85rem] uppercase tracking-[2px] text-[#666] mb-3 font-bold flex items-center gap-2" data-id="element-355" data-mp="1jr5pctnyblst">
            <CheckCircle className="w-4 h-4 text-alphaGold" data-id="element-356" data-mp="1oidyrppa775z" /> BENEFICIOS CLAVE
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-[25px]" data-id="element-357" data-mp="1sjpn7rwf5mcl">
            {product.benefits.map((benefit, idx) => <div key={idx} className="flex items-start gap-2.5 bg-[#1e1e1e] p-3 rounded" data-id="element-358" data-mp="1xh91usx14152" data-mpi={idx}>
                <span className="text-alphaGold font-black text-[1rem] flex-shrink-0 mt-px" data-id="element-359" data-mp="22fss35innz38">
                  ✓
                </span>
                <span className="text-[#ccc] text-[0.85rem] leading-[1.5]" data-id="element-360" data-mp="84e0sk9zxrld">
                  {benefit}
                </span>
              </div>)}
          </div>

          <div className="flex items-center gap-5 mb-[25px] flex-wrap" data-id="element-361" data-mp="23jsxixk9fsmc">
            <span className="text-[2.5rem] font-black text-alphaGold" data-id="element-362" data-mp="23lx981oehvx6">
              ${product.currentPrice.toLocaleString('es-UY')}
            </span>
          </div>

          <div className="bg-alphaGold/10 border border-alphaGold/25 rounded p-[14px_18px] mb-5 text-[0.88rem] text-alphaGold font-semibold text-center flex items-center justify-center gap-2" data-id="element-363" data-mp="2cj7kvo54rn2m">
            <AlertTriangle className="w-4 h-4" data-id="element-364" data-mp="1jwv60egv8e9q" /> Stock limitado · Esta oferta
            puede terminar en cualquier momento
          </div>

          <div className="bg-dark-500 border border-dark-300 rounded p-[15px_18px] mb-5 text-[0.85rem] text-[#999] space-y-2" data-id="element-365" data-mp="1fsf22iqtbgx5">
            <div className="flex items-start gap-2" data-id="element-366" data-mp="1ov6diijoucos">
              <Package className="w-4 h-4 mt-0.5 text-alphaGold flex-shrink-0" data-id="element-367" data-mp="1otlnn68bjhx9" />
              <span data-id="element-368" data-mp="1140zm8xb5uh4">
                <strong className="text-white" data-id="element-369" data-mp="113iu0lora3wj">Envío a domicilio:</strong> desde
                $190 pesos uruguayos, varía según tu zona
              </span>
            </div>
            <div className="flex items-start gap-2" data-id="element-370" data-mp="37sszgkhhz4ek">
              <MapPin className="w-4 h-4 mt-0.5 text-alphaGold flex-shrink-0" data-id="element-371" data-mp="3ck2lzc7r1rm8" />
              <span data-id="element-372" data-mp="3cm5miq6j3357">
                <strong className="text-white" data-id="element-373" data-mp="2yt69cuu7xl0t">Pick Up:</strong> Malvín,
                Montevideo
              </span>
            </div>
          </div>

          <a href={waLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 bg-whatsapp text-white text-[1.05rem] font-extrabold py-[18px] px-[30px] rounded uppercase tracking-[1px] transition-all duration-300 w-full shadow-[0_6px_25px_rgba(37,211,102,0.3)] hover:bg-whatsappDark hover:scale-[1.02]" data-id="element-374" data-mp="33siqr02blzw0">
            <WhatsAppIcon className="w-6 h-6" data-id="element-375" data-mp="3mgnsk1zf3as6" />
            Quiero Este Producto — Pedir por WhatsApp
          </a>
        </div>
      </div>
    </div>;
}